import Phaser from 'phaser';
import Player from '../objects/Player.js';
import OverlayUI from '../ui/OverlayUI.js';


export default class BuildingScene extends Phaser.Scene {
  constructor() {
    super('BuildingScene');
    this.sceneTransitioning = false; // Prevent multiple scene transitions
  }

  init(data) {
    console.log('Entering BuildingScene with data:', data);
    // Reset scene transition flag
    this.sceneTransitioning = false;
    
    // Store door data passed from VillageScene
    this.doorData = data.doorData || null;
    console.log('Door data received:', this.doorData);
    
    // Set building name from door data, fallback to default
    this.building = this.doorData?.name || 'room-mo';
  }

  create() {
    // Create Tiled map for the room
    const map = this.make.tilemap({ key: this.building });

    // Add all tilesets used by room-mo map - match image names from JSON to asset keys
    const roombuilderTileset = map.addTilesetImage('roombuilder', 'roombuilder');
    const genericTileset = map.addTilesetImage('generic', 'generic');
    const bedroomTileset = map.addTilesetImage('bedroom', 'bedroom');
    const livingroomTileset = map.addTilesetImage('livingroom', 'livingroom');
    const artTileset = map.addTilesetImage('art', 'art');
    const gymTileset = map.addTilesetImage('gym', 'gym');
    const basementTileset = map.addTilesetImage('basement', 'basement');
    const kitchenTileset = map.addTilesetImage('kitchen', 'kitchen');
    const halloweenTileset = map.addTilesetImage('halloween', 'halloween');
    const hospitalTileset = map.addTilesetImage('hospital', 'hospital');
    const grocerystoreTileset = map.addTilesetImage('grocerystore', 'grocerystore');
    const modernTileset = map.addTilesetImage('modern', 'tilemap_packed');


    // Create array of all tilesets for layer creation
    const allTilesets = [
      roombuilderTileset, 
      genericTileset, 
      bedroomTileset, 
      livingroomTileset, 
      artTileset, 
      gymTileset, 
      basementTileset, 
      kitchenTileset,
      halloweenTileset,
      hospitalTileset,
      grocerystoreTileset,
      modernTileset
    ];
    
    // Store collision layers
    this.collisionLayers = [];
    
    // Create map layers in proper order (bottom to top)
    try {
      // 1. Floor (no collision)
      const floorLayer = map.createLayer('Floor', allTilesets, 0, 0);
      
      // 2. Walls (with collision)
      const wallsLayer = map.createLayer('Walls', allTilesets, 0, 0);
      if (wallsLayer) {
        wallsLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(wallsLayer);
      }
      
      // 3. Decoration (no collision)
      const decorationLayer = map.createLayer('Decoration', allTilesets, 0, 0);
      
      // 4. DecorationCollides (with collision)
      const decorationCollidesLayer = map.createLayer('DecorationCollides', allTilesets, 0, 0);
      if (decorationCollidesLayer) {
        decorationCollidesLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(decorationCollidesLayer);
      }
      
      // 5. DecorationCollidesPlus (with collision)
      const decorationCollidesPlusLayer = map.createLayer('DecorationCollidesPlus', allTilesets, 0, 0);
      if (decorationCollidesPlusLayer) {
        decorationCollidesPlusLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(decorationCollidesPlusLayer);
      }
      
      // Set up camera bounds based on map size
      const mapWidth = map.widthInPixels;
      const mapHeight = map.heightInPixels;
      this.cameras.main.setBounds(0, 0, mapWidth, mapHeight);
      this.physics.world.setBounds(0, 0, mapWidth, mapHeight);
      
      console.log('Room map loaded successfully with layers:', 
                  map.layers.map(layer => layer.name));
      
    } catch (error) {
      console.error('Error loading room map:', error);
      console.log('Available layers:', map.layers.map(layer => layer.name));
      
    }
    
    // Find spawn position from map objects
    let spawnX = 200; // Default fallback
    let spawnY = 300; // Default fallback
    
    const objectsLayer = map.getObjectLayer('objects');
    if (objectsLayer) {
      const spawnPoint = objectsLayer.objects.find(obj => obj.name === 'spawnHere');
      if (spawnPoint) {
        // Round coordinates to avoid fractional pixel issues
        spawnX = Math.round(spawnPoint.x);
        spawnY = Math.round(spawnPoint.y);
        console.log(`Using spawn point from map: (${spawnX}, ${spawnY}) (rounded from ${spawnPoint.x}, ${spawnPoint.y})`);
      }
    }
    
    // console.log('About to create player...');
    // console.log('Physics world bounds:', this.physics.world.bounds);
    // console.log('Scene state:', this.scene.isActive(), this.scene.isVisible());
    
    try {
      this.player = new Player(this, spawnX, spawnY);
      // console.log('Player created successfully');
      this.player.setDepth(500);
      // console.log('Player depth set');
    } catch (error) {
      console.error('Error creating player:', error);
      console.log('Fallback: creating player at safe coordinates');
      this.player = new Player(this, 200, 200);
      this.player.setDepth(500);
    }
    
    // Set up collisions with all collision layers
    // console.log('Setting up collisions...');
    this.collisionLayers.forEach((layer, index) => {
      console.log(`Setting up collision for layer ${index}: ${layer.layer.name}`);
      try {
        this.physics.add.collider(this.player, layer, (player, tile) => {
          // Optional: console.log(`Player hit ${layer.layer.name} tile:`, tile.index);
        });
        // console.log(`Collision setup complete for layer: ${layer.layer.name}`);
      } catch (error) {
        // console.error(`Error setting up collision for layer ${layer.layer.name}:`, error);
      }
    });
    // console.log('All collisions setup complete');
    
    // Set up room objects and their physics AFTER player is created
    // console.log('Setting up room objects...');
    this.setupRoomObjects(map);
    // console.log('Room objects setup complete');
    
    // Camera follows player
    // console.log('Setting up camera...');
    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    // console.log('Camera setup complete');
    
    // Add fade-in transition effect
    this.createSceneTransition();
    
    // Create persistent overlay UI with navigation buttons
    this.overlayUI = new OverlayUI(this, { showNavigation: true });
    this.overlayUI.create();
  }

  setupRoomObjects(map) {
    // Create physics groups for interactive objects
    this.friendHitboxes = this.physics.add.staticGroup();
    this.exitHitboxes = this.physics.add.staticGroup();
    
    // Load rescued friends from localStorage
    this.rescuedFriends = this.loadRescuedFriends();
    
    // Get the objects layer
    const objectsLayer = map.getObjectLayer('objects');
    
    if (objectsLayer) {
      // console.log(`Found objects layer with ${objectsLayer.objects.length} objects`);
      
      objectsLayer.objects.forEach((obj, index) => {
        // Create invisible physics rectangle for each object
        const objectHitbox = this.add.rectangle(obj.x, obj.y, obj.width, obj.height);
        objectHitbox.setOrigin(0, 0); // Tiled objects use top-left origin
        objectHitbox.setVisible(false); // Make invisible
        objectHitbox.setAlpha(0); // Ensure completely invisible
        
        // Add physics body
        this.physics.add.existing(objectHitbox, true); // true = static body
        
        // Store object data
        objectHitbox.objectData = {
          id: obj.id,
          name: obj.name || `object_${index}`,
          type: obj.type || 'unknown',
          x: obj.x,
          y: obj.y,
          width: obj.width,
          height: obj.height
        };
        
        // Add to appropriate group based on object name/type
        if (obj.name === 'friend' || obj.type === 'friend') {
          // Check if this friend has been rescued
          const friendName = this.getFriendNameFromBuilding();
          const isRescued = this.rescuedFriends.includes(friendName);
          
          if (!isRescued) {
            // Only add hitbox and display friend if they haven't been rescued
            this.friendHitboxes.add(objectHitbox);
            this.createFriendSprite(obj.x, obj.y, friendName);
            // console.log(`Created friend hitbox for ${friendName} at (${obj.x}, ${obj.y})`);
          } else {
            // Friend has been rescued - room is empty, no interaction
            // console.log(`Friend ${friendName} already rescued - room is empty`);
          }
        } else if (obj.name === 'exit' || obj.type === 'exit') {
          this.exitHitboxes.add(objectHitbox);
          // console.log(`Created exit hitbox at (${obj.x}, ${obj.y})`);
        }
      });
      
      // Set up overlaps for player interactions
      if (this.friendHitboxes.children.size > 0) {
        this.physics.add.overlap(this.player, this.friendHitboxes, this.onPlayerMeetFriend, null, this);
      }
      
      if (this.exitHitboxes.children.size > 0) {
        this.physics.add.overlap(this.player, this.exitHitboxes, this.onPlayerExitRoom, null, this);
      }
      
    } else {
      console.log('No objects layer found in room map');
    }
  }
  
  onPlayerMeetFriend(player, friendHitbox) {
    // Prevent multiple simultaneous scene transitions
    if (this.sceneTransitioning) {
      return;
    }
    
    // Check if objects are still valid
    if (!player || !friendHitbox || !player.body || !friendHitbox.body) {
      console.warn('Invalid objects in friend collision');
      return;
    }
    
    this.sceneTransitioning = true;
    
    const friendData = friendHitbox.objectData;
    console.log(`Player met friend: ${friendData.name}`);
    
    // Use Phaser's native camera fade out
    this.cameras.main.fadeOut(500, 0, 0, 0);
    
    // Listen for fade out complete event
    this.cameras.main.once(
      Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
      () => {
        this.scene.start('FriendScene', { 
          friendData: friendData, 
          doorData: this.doorData 
        });
      }
    );
  }
  
  onPlayerExitRoom(player, exitHitbox) {
    // Prevent multiple simultaneous scene transitions
    if (this.sceneTransitioning) {
      return;
    }
    
    // Check if objects are still valid
    if (!player || !exitHitbox || !player.body || !exitHitbox.body) {
      console.warn('Invalid objects in exit collision');
      return;
    }
    
    this.sceneTransitioning = true;
    
    const exitData = exitHitbox.objectData;
    console.log(`Player exited room via: ${exitData.name}`);
    
    // Use Phaser's native camera fade out
    this.cameras.main.fadeOut(500, 0, 0, 0);
    
    // Listen for fade out complete event
    this.cameras.main.once(
      Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
      () => {
        // Return to village scene after fade completes
        this.scene.start('VillageScene', { fromBuilding: true });
      }
    );
  }


  createSceneTransition() {
    // Use Phaser's native camera fade in
    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  update() {
    // Player movement is handled by the Player class update method
    if (this.player) {
      this.player.update();
    }
  }

  getFriendNameFromBuilding() {
    // Extract friend name from building name (e.g., "room-mo" -> "mo")
    if (this.building && this.building.includes('-')) {
      return this.building.split('-')[1];
    }
    return 'mo'; // fallback
  }

  createFriendSprite(x, y, friendName) {
    // Create friend sprite at hitbox location using hypno state
    const friendSprite = this.add.image(x + 32, y + 32, `${friendName}-room-hypno`);
    friendSprite.setOrigin(0.5); // Center the 64x64 image on the hitbox center
    friendSprite.setDepth(400); // Below player but above room
    
    console.log(`Created friend sprite for ${friendName} at (${x + 32}, ${y + 32})`);
    this.addHypnoEffect(x + 32, y + 32);
    
    return friendSprite;
  }
  addHypnoEffect(posX, posY) {
    // Placeholder for adding hypnotic visual effects if needed
    this.hypnoEffect = this.add.image(posX, posY, 'hypno');
    this.tweens.add({
      targets: this.hypnoEffect,
      ease: 'Linear',
      rotation: 20,
      scale: [0.8, 1.2, 0.8],
      repeat: -1,
      duration: 10000
    });
    this.hypnoEffect.setScale(1);
    this.hypnoEffect.setDepth(29); // Above all other elements
  }

  loadRescuedFriends() {
    // Load rescued friends from localStorage (same as OverlayUI)
    try {
      const saved = localStorage.getItem('hypnobreaker_rescued_friends');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading rescued friends:', error);
      return [];
    }
  }

  shutdown() {
    // Clean up overlay UI
    if (this.overlayUI) {
      this.overlayUI.destroy();
      this.overlayUI = null;
    }
    
    // Clean up physics groups and prevent further collision detection
    if (this.friendHitboxes) {
      this.friendHitboxes.clear(true, true);
    }
    if (this.exitHitboxes) {
      this.exitHitboxes.clear(true, true);
    }
    
    // Remove all physics colliders/overlaps
    if (this.physics && this.physics.world) {
      this.physics.world.removeAllListeners();
    }
    this.player.destroy();
    this.player = null;
    
    this.sceneTransitioning = false;
  }
}